
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, Output, ViewChild } from '@angular/core';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter, map, take } from 'rxjs';
import { MatFormFieldCtrlImplDirective } from '../directives/editor-form-field.directive';
import { ValueAccessorImplDirective } from '../directives/value-accessor-impl.directive';
import { FormControlService } from '../form-control.service';
import { FormControlsUtilsModule } from '../form-controls-utils';
import { DisableMatOptionSelectionDirective } from './disable-mat-option-selection.directive';
import { MultiAutocompleteFormControlService } from './multi-autocomplete-form-control.service';

@Component({
  selector: 'ss-multi-autocomplete-form-control',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatCheckboxModule,
    MatFormFieldCtrlImplDirective,
    FormControlsUtilsModule,
    DisableMatOptionSelectionDirective
  ],
  providers: [MultiAutocompleteFormControlService],
  templateUrl: './multi-autocomplete-form-control.component.html',
  styleUrls: ['./multi-autocomplete-form-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ValueAccessorImplDirective]
})
export class MultiAutocompleteFormControlComponent<T extends any> {

  readonly autocompleteSubject = this._multiAutocompleteFormControlService.autocompleteSubject;
  readonly autocompleteTriggerSubject = this._multiAutocompleteFormControlService.autocompleteTriggerSubject;

  readonly fetchingSubject = this._multiAutocompleteFormControlService.fetchingSubject;
  readonly dataSubject = this._multiAutocompleteFormControlService.dataSubject;
  readonly displayWithObs = this._multiAutocompleteFormControlService.displayWithObs;
  readonly displayedDataObs = this._multiAutocompleteFormControlService.displayedDataObs;
  readonly inputSubject = this._multiAutocompleteFormControlService.inputSubject;
  readonly focusedSubject = this._multiAutocompleteFormControlService.focusedSubject;
  readonly valueSubject = this._formControlService.valueSubject;
  readonly touchedSubject = this._formControlService.touchedSubject;
  readonly readOnlyObs = this._formControlService.readOnlyObs;
  readonly labelObs = this._formControlService.labelObs

  readonly dataFieldSubject = this._formControlService.dataFieldSubject
  @Input() get dataField() {

    return this.dataFieldSubject.value
  }
  set dataField(value) {

    this.dataFieldSubject.next(value)
  }


  @Output('selectedItemsChange')
  readonly selectedItemsSubject = this._multiAutocompleteFormControlService.selectedItemsSubject
  get selectedItems() {
    return this.selectedItemsSubject.value
  }

  readonly valueExprSubject = this._multiAutocompleteFormControlService.valueExprSubject
  @Input() get valueExpr() {
    return this.valueExprSubject.value
  }
  set valueExpr(value) {
    if (!value) {
      return;
    }

    this.valueExprSubject.next(value);
  }

  readonly displayExprSubject = this._multiAutocompleteFormControlService.displayExprSubject;
  @Input() get displayExpr() {
    return this.displayExprSubject.value
  }
  set displayExpr(value) {
    if (!value) {
      return;
    }

    this.displayExprSubject.next(value)
  }

  readonly loadFnSubject = this._multiAutocompleteFormControlService.loadFnSubject;
  @Input() get loadFn() {
    return this.loadFnSubject.value
  }
  set loadFn(value) {

    if (!value) {
      return
    }

    this.loadFnSubject.next(value)
  }

  readonly initialSelectedItemsSubject = this._multiAutocompleteFormControlService.initialSelectedItemsSubject;
  @Input() get initialSelectedItems() {
    return this.initialSelectedItemsSubject.value
  }
  set initialSelectedItems(value) {
    this.initialSelectedItemsSubject.next(value)
  }


  @ViewChild('autocomplete', { read: MatAutocomplete })
  private _autocomplete: MatAutocomplete

  @ViewChild('input', { read: ElementRef })
  private _input: ElementRef<HTMLInputElement>

  @ViewChild('input', { read: MatAutocompleteTrigger })
  private _autocompleteTrigger: MatAutocompleteTrigger


  constructor(
    private readonly _multiAutocompleteFormControlService: MultiAutocompleteFormControlService,
    private readonly _formControlService: FormControlService
  ) { }


  ngAfterViewInit() {


    this._formControlService.htmlElementSubject.next(this._input.nativeElement);

    this._multiAutocompleteFormControlService.initialize();
    this.autocompleteSubject.next(this._autocomplete)
    this.autocompleteTriggerSubject.next(this._autocompleteTrigger);
    this.inputSubject.next(this._input);
  }

  trackByValue(idx: number, item: any) {

    return item[this.valueExpr]
  }

  remove(selectedItem: T) {

    this._multiAutocompleteFormControlService.selectedItems = this.selectedItems.filter((item) => item !== selectedItem)

  }


  isSelectedObs(item: T) {

    return this.selectedItemsSubject.pipe(
      take(1),
      map((selectedItems) => selectedItems?.find(innerItem => item === innerItem))
    )
  }




  select(item: T) {
    this._multiAutocompleteFormControlService.select(item)
  }

  getDisplayObs(item: T) {
    return this.displayExprSubject.pipe(
      filter(Boolean),
      map((displayExpr) => (item as any)[displayExpr])
    )
  }
}
